import React, { useMemo } from "react";
import { Pressable, StyleProp, StyleSheet, View, ViewStyle } from "react-native";
import { useTheme } from "../hooks/use-theme";
import Animated, { interpolate, useAnimatedStyle, SharedValue } from "react-native-reanimated";

const DOT_GAP = 9;
export const DOT_SIZE = 11;

type DotsProps = {
    totalDots: number;
    currentDot: SharedValue<number>;
    style?: StyleProp<ViewStyle>;
    color?: string;
    onPress?: (index: number) => void;
};

const Dots = ({ totalDots, currentDot, style, color, onPress }: DotsProps) => {
    const styles = useStyles();

    const animStyle = useAnimatedStyle(() => {
        const width = DOT_SIZE + DOT_GAP;
        const length = totalDots - 1;

        const inputRange = [0, length, length + 1];
        const outputRange = [0, width * length, width * length + DOT_SIZE];

        return {
            transform: [
                {
                    translateX: interpolate(currentDot.value, inputRange, outputRange),
                },
            ],
        };
    }, [currentDot, totalDots]);

    const prevAnimStyle = useAnimatedStyle(() => {
        const inputRange = [totalDots - 1, totalDots - 1, totalDots];
        const outputRange = [-DOT_SIZE, -DOT_SIZE, 0];

        return {
            transform: [
                {
                    translateX: interpolate(currentDot.value, inputRange, outputRange),
                },
            ],
        };
    }, [currentDot, totalDots]);

    const dotsCommonStyle = useMemo(
        () => [styles.dot, styles.absoluteDot, color ? { backgroundColor: color } : {}],
        [color, styles.absoluteDot, styles.dot],
    );

    return (
        <View style={[styles.dotsContainer, style]}>
            {[...Array(totalDots).keys()].map((index) => (
                <Pressable
                    key={index}
                    onPress={() => {
                        onPress?.(index);
                    }}
                    style={[
                        styles.dot,
                        {
                            marginLeft: index !== 0 ? DOT_GAP : 0,
                        },
                    ]}
                />
            ))}
            <Animated.View style={[dotsCommonStyle, prevAnimStyle]} />
            <Animated.View style={[dotsCommonStyle, animStyle]} />
        </View>
    );
};

const useStyles = () => {
    const { colors } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                dotsContainer: {
                    position: "relative",
                    display: "flex",
                    flexDirection: "row",
                    overflow: "hidden",
                },
                dot: {
                    width: DOT_SIZE,
                    height: DOT_SIZE,
                    borderRadius: DOT_SIZE / 2,
                    backgroundColor: colors.backdrop,
                },
                absoluteDot: {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    backgroundColor: colors.primary,
                },
            }),
        [colors],
    );
};

export default Dots;
